import React, { useState } from 'react';
import * as Yup from 'yup';
import { Container, Box, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  FormContainer,
  LogoContainer,
  StyledTextField,
  BackgroundContainer,
  Overlay,
  LogoImage,
  ContentContainer,
} from './styles';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Button from '@mui/material/Button';
import logo from '../../assets/img/logo_v3.png';
import useAuth from '../../hooks/useAuth';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const { signIn } = useAuth();
  const [searchParams] = useSearchParams();

  // Captura o redirect que veio via query string (se existir).
  const redirectUrl = searchParams.get('redirect') || undefined;

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Por favor, insira um e-mail válido.')
      .required('O e-mail é obrigatório.'),
    password: Yup.string()
      .min(6, 'A senha deve ter pelo menos 6 caracteres.')
      .required('A senha é obrigatória.'),
  });

  const validateField = (field: string, value: string) => {
    validationSchema
      .validateAt(field, { [field]: value })
      .then(() => setErrors((prevErrors) => ({ ...prevErrors, [field]: undefined })))
      .catch((err) =>
        setErrors((prevErrors) => ({ ...prevErrors, [field]: err.message })),
      );
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'email') {
      setEmail(value);
    } else if (field === 'password') {
      setPassword(value);
    }
    validateField(field, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await validationSchema.validate({ email, password }, { abortEarly: false });
      setLoading(true);
      setErrors({});

      // Agora passamos redirectUrl como 2º parâmetro de signIn
      await signIn({ email, password }, redirectUrl);
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { email?: string; password?: string } = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          const path = error.path as keyof typeof newErrors;
          if (path) {
            newErrors[path] = error.message;
          }
        });
        setErrors(newErrors);
      } else {
        setErrors({
          email: 'Erro ao tentar fazer login. Verifique suas credenciais.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <BackgroundContainer>
      <Overlay />
      <Container
        maxWidth="xs"
        sx={{ display: 'flex', alignItems: 'center', minHeight: '100vh', zIndex: 1 }}
      >
        <FormContainer>
          <LogoContainer>
            <LogoImage src={logo} alt="Logo" />
          </LogoContainer>
          <ContentContainer>
            <form onSubmit={handleSubmit}>
              <StyledTextField
                label="Email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email}
                InputProps={{
                  startAdornment: <EmailIcon color="action" />,
                }}
              />
              <StyledTextField
                label="Senha"
                variant="outlined"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={Boolean(errors.password)}
                helperText={errors.password}
                InputProps={{
                  startAdornment: <LockIcon color="action" />,
                }}
              />
              <Box mt={2} sx={{ position: 'relative' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="medium"
                  type="submit"
                  disabled={loading}
                  sx={{
                    paddingY: 1.5,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <FingerprintIcon /> Entrar
                    </>
                  )}
                </Button>
              </Box>
            </form>
          </ContentContainer>
        </FormContainer>
      </Container>
    </BackgroundContainer>
  );
};

export default Login;